import type { CircleShape, Point, Shape } from '../types'

/**
 * Convert raw points string to array of Point objects
 * Format: "x1//y1,x2//y2,x3//y3"
 */
export function rawPointsToPoints(rawPoints: string): Point[] {
  if (!rawPoints || rawPoints.trim() === '') {
    return []
  }
  
  const pointPairs = rawPoints.split(',')
  return pointPairs.map(pair => {
    const [x, y] = pair.split('//').map(coord => parseFloat(coord.trim()))
    return { x, y }
  })
}

/**
 * Convert array of Point objects to raw points string
 * Format: "x1//y1,x2//y2,x3//y3"
 */
export function pointsToRawPoints(points: Point[]): string {
  return points.map(point => `${point.x}//${point.y}`).join(',')
}

/**
 * Convert raw shape data to Shape object for drawing
 */
export interface RawShapeData {
  id: string
  type: 'line' | 'square' | 'circle' | 'freehand'
  points: string // raw points format
  label?: string // Display label (e.g., "Amniotic Fluid")
  dbId?: number | string // Database ID for existing segmentations
  backendLabel?: string | null // Backend: MEAS__* for measurements, or resolved category code for structures
  backendType?: string // Original backend type (e.g., "POLYLINE", "ELLIPSE")
  /** Source of truth for structure; sent on save when label is null */
  categoryId?: number | null
  /** "structure" | "measurement"; backend derives from label if omitted */
  segmentType?: 'structure' | 'measurement'
  /** True = drawn by user; false = AI-generated. Controls visual style (solid vs dashed). */
  isManual?: boolean
}

export function rawShapeToShape(rawShape: RawShapeData): Shape {
  const points = rawPointsToPoints(rawShape.points)
  
  // Common properties to preserve
  const commonProps = {
    label: rawShape.label,
    dbId: rawShape.dbId,
    backendLabel: rawShape.backendLabel,
    backendType: rawShape.backendType,
    categoryId: rawShape.categoryId,
    segmentType: rawShape.segmentType,
    isManual: rawShape.isManual,
  }
  
  switch (rawShape.type) {
    case 'line':
      if (points.length < 2) {
        throw new Error('Line requires at least 2 points')
      }
      return {
        id: rawShape.id,
        type: 'line',
        startPoint: points[0],
        endPoint: points[1],
        ...commonProps
      }
    
    case 'square':
      if (points.length < 2) {
        throw new Error('Square requires at least 2 points')
      }
      return {
        id: rawShape.id,
        type: 'square',
        startPoint: points[0],
        endPoint: points[1],
        ...commonProps
      }
    
    case 'circle': {
      if (points.length < 2) {
        throw new Error('Circle requires center and radius point')
      }
      if (points.length >= 3) {
        const center = points[0]
        const majorEnd = points[1]
        const minorEnd = points[2]
        const radiusX = Math.hypot(majorEnd.x - center.x, majorEnd.y - center.y)
        const radiusY = Math.hypot(minorEnd.x - center.x, minorEnd.y - center.y)
        const rotation = Math.atan2(majorEnd.y - center.y, majorEnd.x - center.x)
        return {
          id: rawShape.id,
          type: 'circle',
          center,
          radiusX,
          radiusY,
          rotation,
          ...commonProps
        }
      }
      const center = points[0]
      const radiusPoint = points[1]
      // Optional 3rd point encodes rotation (x = angle in radians, y = 0)
      const rotation = points.length >= 3 ? points[2].x : 0
      return {
        id: rawShape.id,
        type: 'circle',
        center: center,
        radiusX: Math.abs(radiusPoint.x - center.x),
        radiusY: Math.abs(radiusPoint.y - center.y),
        rotation,
        ...commonProps
      }
    }
    
    case 'freehand':
      return {
        id: rawShape.id,
        type: 'freehand',
        points: points,
        ...commonProps
      }
    
    default:
      throw new Error(`Unknown shape type: ${rawShape.type}`)
  }
}

/**
 * Convert Shape object to raw shape data for saving
 */
export function shapeToRawShape(shape: Shape): RawShapeData {
  let points: Point[] = []
  
  switch (shape.type) {
    case 'line':
      points = [shape.startPoint, shape.endPoint]
      break
    
    case 'square':
      points = [shape.startPoint, shape.endPoint]
      break
    
    case 'circle': {
      const c = shape as CircleShape
      if (c.rotation != null && Number.isFinite(c.rotation)) {
        const cos = Math.cos(c.rotation)
        const sin = Math.sin(c.rotation)
        points = [
          c.center,
          { x: c.center.x + c.radiusX * cos, y: c.center.y + c.radiusX * sin },
          {
            x: c.center.x - c.radiusY * sin,
            y: c.center.y + c.radiusY * cos,
          },
        ]
      } else {
        points = [
          c.center,
          { x: c.center.x + c.radiusX, y: c.center.y + c.radiusY },
        ]
      }
      break
    }
    
    case 'freehand':
      points = shape.points
      break
  }
  
  return {
    id: shape.id,
    type: shape.type,
    points: pointsToRawPoints(points)
  }
}

/**
 * Convert multiple shapes to raw format
 */
export function shapesToRawShapes(shapes: Shape[]): RawShapeData[] {
  return shapes.map(shapeToRawShape)
}

/**
 * Convert multiple raw shapes to Shape objects
 */
export function rawShapesToShapes(rawShapes: RawShapeData[]): Shape[] {
  return rawShapes.map(rawShapeToShape)
}

